import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Layout, Button, Tag, Spin, Space, Typography, message, Input, Empty,
} from 'antd';
import {
  ArrowLeftOutlined, EditOutlined, CopyOutlined, CheckCircleFilled,
  LinkOutlined, ReloadOutlined, ClockCircleOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import AppHeader from './AppHeader';
import { BUILDER_API, EXECUTOR_API, AgentSummary } from '../types';
import { buildAuthHeaders } from '../utils/userStore';
import { colors } from '../theme';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

// ── Component catalog ─────────────────────────────────────────────────────────

interface ComponentMeta {
  key: string;
  label: string;
  emoji: string;
  color: string;
  what: string;
}

const COMPONENTS: ComponentMeta[] = [
  {
    key: 'goal', label: 'Objetivo', emoji: '🎯', color: '#4f9eff',
    what: 'Define la misión del agente: qué debe lograr, cómo sabremos que lo ha conseguido y qué límites no debe cruzar.',
  },
  {
    key: 'sense', label: 'Percepción', emoji: '👁️', color: '#52c41a',
    what: 'Cómo recibe el agente la información del mundo: el tipo de entrada que procesa y si debe identificar un contacto.',
  },
  {
    key: 'reason', label: 'Razón', emoji: '🧠', color: '#faad14',
    what: 'El cerebro analítico: qué modelo de IA usa para interpretar la entrada y con qué estructura devuelve sus conclusiones.',
  },
  {
    key: 'memory', label: 'Memoria', emoji: '💾', color: '#b37feb',
    what: 'Qué recuerda el agente: contexto dentro de una ejecución (corto plazo) y datos que persisten entre ejecuciones (largo plazo).',
  },
  {
    key: 'plan', label: 'Plan', emoji: '📋', color: '#36cfc9',
    what: 'Las acciones entre las que el agente puede elegir tras razonar, antes de actuar.',
  },
  {
    key: 'act', label: 'Acción', emoji: '⚡', color: '#ff7875',
    what: 'Lo que el agente ejecuta de verdad: guardar datos, llamar APIs, notificar… el impacto real en el mundo.',
  },
  {
    key: 'coordinate', label: 'Coordinación', emoji: '🔗', color: '#f759ab',
    what: 'Delegación a agentes especialistas que trabajan en paralelo para enriquecer el resultado.',
  },
];

const CONTAINER  = 460;
const CENTER     = CONTAINER / 2;
const RADIUS     = 165;
const ITEM_SIZE  = 72;
const ANIM_SPEED = '28s';

const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const DASH_SEGMENTS = 60;
const SEGMENT_LEN   = CIRCUMFERENCE / DASH_SEGMENTS;
const DASH_LEN      = SEGMENT_LEN * 0.55;
const GAP_LEN       = SEGMENT_LEN * 0.45;

// Orbital node positions — pure geometry, computed once (not per render).
const HALF_ITEM = ITEM_SIZE / 2;
const ORBIT_POSITIONS = COMPONENTS.map((_, i) => {
  const angle = (2 * Math.PI * i / COMPONENTS.length) - Math.PI / 2;
  return {
    x: CENTER + RADIUS * Math.cos(angle),
    y: CENTER + RADIUS * Math.sin(angle),
  };
});

// ── Natural language descriptions ─────────────────────────────────────────────

const INPUT_TYPE_LABELS: Record<string, string> = {
  meeting_transcript: 'Procesa transcripciones de reuniones',
  aircall_webhook:    'Recibe notificaciones de llamadas de Aircall en tiempo real',
  free_text:          'Acepta texto libre como entrada',
  json:               'Procesa datos en formato JSON',
};
const MODEL_LABELS: Record<string, string> = {
  'gemini-2.5-flash': 'Gemini 2.5 Flash (Google)',
  'gpt-4o':           'GPT-4o (OpenAI)',
  'qwen2.5:7b':       'Qwen 2.5 7B (local)',
};
const SCHEMA_LABELS: Record<string, string> = {
  doubts:     'Extrae y clasifica las dudas del cliente',
  budget:     'Genera un presupuesto estructurado',
  extraction: 'Extrae información estructurada de la entrada',
  custom:     'Produce una salida con formato personalizado',
};
const LTM_LABELS: Record<string, string> = {
  none:     'Sin memoria persistente entre ejecuciones',
  bigquery: 'Persiste datos históricos en BigQuery',
  mongodb:  'Guarda y consulta datos en MongoDB entre ejecuciones',
};
const ACTION_LABELS: Record<string, string> = {
  persist_bigquery: 'Guardar resultados en BigQuery',
  enrich_lead:      'Enriquecer el perfil del lead',
  notify:           'Enviar notificaciones',
  http_request:     'Llamar a APIs externas',
};
const ACT_LABELS: Record<string, string> = {
  persist_bigquery: 'Guarda los resultados en BigQuery',
  enrich_lead:      'Enriquece el perfil del lead',
  notify:           'Envía notificaciones',
  http_request:     'Realiza llamadas a APIs externas',
};
const SPECIALIST_LABELS: Record<string, string> = {
  lead_enrichment: 'Delega en el agente de enriquecimiento de leads',
  estimate_budget: 'Delega en el agente de estimación de presupuesto',
};

function toNaturalLines(compKey: string, value: Record<string, unknown>): string[] {
  const lines: string[] = [];
  switch (compKey) {
    case 'goal': {
      const obj = value.objective as string | undefined;
      if (obj) lines.push(`Misión: ${obj}`);
      const sc = value.success_criteria as string[] | undefined;
      if (sc?.length) lines.push(`Éxito cuando: ${sc.join(', ')}`);
      const con = value.constraints as string[] | undefined;
      if (con?.length) lines.push(`Restricciones: ${con.join(', ')}`);
      break;
    }
    case 'sense': {
      const it = value.input_type as string | undefined;
      if (it) lines.push(INPUT_TYPE_LABELS[it] ?? `Tipo de entrada: ${it}`);
      if (value.extract_contact === true) lines.push('Identifica y extrae el contacto de cada entrada');
      break;
    }
    case 'reason': {
      const m = value.model as string | undefined;
      if (m) lines.push(`Razona con ${MODEL_LABELS[m] ?? m}`);
      const os = value.output_schema as string | undefined;
      if (os) lines.push(SCHEMA_LABELS[os] ?? `Esquema de salida: ${os}`);
      const xi = value.extra_instructions as string | undefined;
      if (xi) lines.push(`Instrucciones adicionales: "${xi}"`);
      break;
    }
    case 'memory': {
      if (value.short_term === true) lines.push('Mantiene contexto durante la ejecución');
      const lts = value.long_term_source as string | undefined;
      if (lts) lines.push(LTM_LABELS[lts] ?? `Fuente de memoria: ${lts}`);
      const lt = value.long_term_table as string | undefined;
      if (lt) lines.push(`Tabla / colección: ${lt}`);
      break;
    }
    case 'plan': {
      const aa = value.available_actions as string[] | undefined;
      if (aa?.length) lines.push(`Puede elegir entre: ${aa.map(a => ACTION_LABELS[a] ?? a).join(', ')}`);
      break;
    }
    case 'act': {
      const acts = value.actions as string[] | undefined;
      if (acts?.length) acts.forEach(a => lines.push(ACT_LABELS[a] ?? a));
      break;
    }
    case 'coordinate': {
      const specs = value.specialists as string[] | undefined;
      if (specs?.length) specs.forEach(s => lines.push(SPECIALIST_LABELS[s] ?? s));
      break;
    }
  }
  return lines;
}

// ── Execution types ───────────────────────────────────────────────────────────

interface ExecutionSummary {
  run_id: string;
  status: 'success' | 'error';
  started_at: string;
  finished_at: string;
  duration_ms: number;
}

interface CognitiveNodeOutputs {
  perception?: Record<string, unknown>;
  reasoning?:  Record<string, unknown>;
  enrichments?: Record<string, unknown>;
  plan?: string[];
  act_result?:  Record<string, unknown>;
  output?: Record<string, unknown>;
}

interface ExecutionDetail extends ExecutionSummary {
  input: string;
  node_outputs: CognitiveNodeOutputs | null;
  error: string | null;
}

// ── Execution helpers ─────────────────────────────────────────────────────────

function formatDuration(ms: number): string {
  if (ms < 1000)  return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function formatRelativeTime(isoStr: string): string {
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'ahora mismo';
  if (mins < 60) return `hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `hace ${hrs}h`;
  return `hace ${Math.floor(hrs / 24)}d`;
}

function durationColor(ms: number): string {
  if (ms < 5000)  return '#52c41a';
  if (ms < 30000) return '#fa8c16';
  return '#ff4d4f';
}

// ── Doubts panel ──────────────────────────────────────────────────────────────

interface Doubt { text?: string; description?: string; category?: string; priority?: string }

const PRIORITY_META: Record<string, { bg: string; border: string; text: string; label: string; dot: string }> = {
  high:   { bg: '#fff1f0', border: '#ffa39e', text: '#cf1322', label: 'Alta',  dot: '#ff4d4f' },
  medium: { bg: '#fff7e6', border: '#ffd591', text: '#d46b08', label: 'Media', dot: '#fa8c16' },
  low:    { bg: '#f6ffed', border: '#b7eb8f', text: '#389e0d', label: 'Baja',  dot: '#52c41a' },
};

function DoubtsPanel({ doubts }: { doubts: Doubt[] }) {
  if (!doubts.length) return null;
  return (
    <div>
      <Text strong style={{ fontSize: 12, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 12 }}>
        🧠 {doubts.length} duda{doubts.length !== 1 ? 's' : ''} extraída{doubts.length !== 1 ? 's' : ''}
      </Text>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {doubts.map((d, i) => {
          const prio   = (d.priority ?? 'medium').toLowerCase();
          const colors = PRIORITY_META[prio] ?? PRIORITY_META.medium;
          const text   = d.text ?? d.description ?? '';
          return (
            <div key={i} style={{
              background: colors.bg,
              border: `1.5px solid ${colors.border}`,
              borderRadius: 10,
              padding: '12px 14px',
              flex: '1 1 180px',
              maxWidth: 280,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: colors.dot, flexShrink: 0 }} />
                <Tag style={{ margin: 0, fontSize: 10, fontWeight: 600, color: colors.text, background: 'transparent', borderColor: colors.border }}>
                  {colors.label}
                </Tag>
                {d.category && (
                  <Tag style={{ margin: 0, fontSize: 10, background: '#f5f5f5', color: '#595959', border: '1px solid #e8e8e8' }}>
                    {d.category}
                  </Tag>
                )}
              </div>
              <Text style={{ fontSize: 13, color: '#262626', lineHeight: 1.55 }}>{text}</Text>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Phase pipeline ─────────────────────────────────────────────────────────────

interface PhaseMeta { key: string; emoji: string; label: string; summary: string; color: string }

function buildPhases(no: CognitiveNodeOutputs): PhaseMeta[] {
  const out: PhaseMeta[] = [];

  if (no.perception) {
    const p = no.perception;
    const phone = p.phone as string | undefined;
    const raw   = p.raw as string | undefined;
    out.push({
      key: 'perception', emoji: '👁️', label: 'Percepción', color: '#52c41a',
      summary: phone ? `Tel: ${phone}` : raw ? `${raw.slice(0, 40)}…` : 'Entrada recibida',
    });
  }

  if (no.reasoning) {
    const doubts = (no.reasoning.doubts as Doubt[] | undefined) ?? [];
    out.push({
      key: 'reasoning', emoji: '🧠', label: 'Razonamiento', color: '#faad14',
      summary: doubts.length
        ? `${doubts.length} duda${doubts.length !== 1 ? 's' : ''} encontrada${doubts.length !== 1 ? 's' : ''}`
        : 'Análisis completado',
    });
  }

  if (no.enrichments && Object.keys(no.enrichments).length > 0) {
    const le = (no.enrichments as Record<string, Record<string, unknown>>).lead_enrichment;
    out.push({
      key: 'enrichments', emoji: '🔗', label: 'Coordinación', color: '#f759ab',
      summary: le?.found === true ? 'Lead encontrado' : le?.found === false ? 'Lead no encontrado' : 'Especialistas consultados',
    });
  }

  if (no.act_result && Object.keys(no.act_result).length > 0) {
    const bq = (no.act_result as Record<string, Record<string, unknown>>).persist_bigquery;
    out.push({
      key: 'act', emoji: '⚡', label: 'Acción', color: '#ff7875',
      summary: bq?.rows_inserted != null
        ? `BigQuery: ${bq.rows_inserted} fila${Number(bq.rows_inserted) !== 1 ? 's' : ''}`
        : 'Acciones ejecutadas',
    });
  }

  return out;
}

// ── Execution detail panel ────────────────────────────────────────────────────

function ExecutionDetailPanel({ detail }: { detail: ExecutionDetail }) {
  const { node_outputs, input, error, duration_ms, started_at, status } = detail;
  const phases   = node_outputs ? buildPhases(node_outputs) : [];
  const doubts   = (node_outputs?.reasoning?.doubts as Doubt[] | undefined) ?? [];
  const bq       = (node_outputs?.act_result as Record<string, Record<string, unknown>> | undefined)?.persist_bigquery;
  const le       = (node_outputs?.enrichments as Record<string, Record<string, unknown>> | undefined)?.lead_enrichment;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Execution meta */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Tag color={status === 'success' ? 'success' : 'error'} style={{ fontWeight: 600 }}>
          {status === 'success' ? '✓ Éxito' : '✗ Error'}
        </Tag>
        <Text type="secondary" style={{ fontSize: 12 }}>
          <ClockCircleOutlined style={{ marginRight: 4 }} />
          {new Date(started_at).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' })}
        </Text>
        <Tag style={{ background: `${durationColor(duration_ms)}15`, borderColor: `${durationColor(duration_ms)}40`, color: durationColor(duration_ms) }}>
          <ThunderboltOutlined style={{ marginRight: 3 }} />{formatDuration(duration_ms)}
        </Tag>
      </div>

      {/* Phase pipeline */}
      {phases.length > 0 && (
        <div>
          <Text strong style={{ fontSize: 12, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 12 }}>
            Pipeline de ejecución
          </Text>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
            {phases.map((phase, i) => (
              <React.Fragment key={phase.key}>
                <div style={{
                  background: '#fff',
                  border: `1.5px solid ${phase.color}44`,
                  borderRadius: 10,
                  padding: '10px 14px',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  minWidth: 100,
                  boxShadow: `0 2px 8px ${phase.color}18`,
                }}>
                  <span style={{ fontSize: 20, marginBottom: 4 }}>{phase.emoji}</span>
                  <Text strong style={{ fontSize: 11, color: '#262626' }}>{phase.label}</Text>
                  <Text style={{ fontSize: 10, color: '#8c8c8c', textAlign: 'center', marginTop: 2, maxWidth: 90 }}>
                    {phase.summary}
                  </Text>
                </div>
                {i < phases.length - 1 && (
                  <span style={{ color: '#bfbfbf', fontSize: 18 }}>→</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 10, padding: '12px 16px' }}>
          <Text strong style={{ color: '#cf1322', fontSize: 13 }}>❌ Error: </Text>
          <Text style={{ color: '#cf1322', fontSize: 13, fontFamily: 'monospace' }}>{error}</Text>
        </div>
      )}

      {/* Doubts */}
      {doubts.length > 0 && <DoubtsPanel doubts={doubts} />}

      {/* Stats row: BQ + Lead */}
      {(bq || le) && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {bq && (
            <div style={{
              background: '#f6ffed', border: '1.5px solid #b7eb8f', borderRadius: 10,
              padding: '14px 20px', display: 'flex', gap: 24, alignItems: 'center',
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#389e0d', lineHeight: 1 }}>
                  {String(bq.rows_inserted ?? 0)}
                </div>
                <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>filas en BigQuery</div>
              </div>
              {!!bq.table && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 12, fontFamily: 'monospace', color: '#389e0d', fontWeight: 600 }}>
                    {String(bq.table)}
                  </div>
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>tabla</div>
                </div>
              )}
              {!!bq.meeting_id && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, fontFamily: 'monospace', color: '#52c41a' }}>
                    {String(bq.meeting_id).slice(0, 8)}…
                  </div>
                  <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>meeting_id</div>
                </div>
              )}
            </div>
          )}
          {le && (
            <div style={{
              background: le.found ? '#f6ffed' : '#fff7e6',
              border: `1.5px solid ${le.found ? '#b7eb8f' : '#ffd591'}`,
              borderRadius: 10, padding: '14px 20px',
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: le.found ? '#389e0d' : '#d46b08', marginBottom: 4 }}>
                {le.found ? '✓ Lead identificado' : '⚠ Lead no encontrado'}
              </div>
              {!!le.matched_by && (
                <div style={{ fontSize: 11, color: '#8c8c8c' }}>por {String(le.matched_by)}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Raw input */}
      <details>
        <summary style={{
          fontSize: 12, color: '#8c8c8c', cursor: 'pointer',
          userSelect: 'none', marginBottom: 6, listStyle: 'none',
        }}>
          <span style={{ borderBottom: '1px dashed #d9d9d9' }}>Ver input original ▾</span>
        </summary>
        <pre style={{
          fontSize: 11, background: '#fafafa', border: '1px solid #f0f0f0',
          borderRadius: 8, padding: '10px 14px', overflowX: 'auto', marginTop: 8,
          maxHeight: 220, overflowY: 'auto', margin: 0,
          fontFamily: 'monospace', color: '#595959', lineHeight: 1.5,
        }}>
          {input.length > 2000 ? input.slice(0, 2000) + '\n…(truncado)' : input}
        </pre>
      </details>
    </div>
  );
}

// ── Executions tab ────────────────────────────────────────────────────────────

function ExecutionsTab({ agentId }: { agentId: string }) {
  const [list, setList]           = useState<ExecutionSummary[]>([]);
  const [total, setTotal]         = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [selected, setSelected]   = useState<string | null>(null);
  const [detail, setDetail]       = useState<ExecutionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    try {
      const res  = await fetch(`${EXECUTOR_API}/agent/builder/${agentId}/executions?limit=30`, {
        headers: buildAuthHeaders(),
      });
      const data = await res.json();
      setList(data.executions ?? []);
      setTotal(data.total ?? 0);
      if (data.executions?.length && !selected) {
        setSelected(data.executions[0].run_id);
      }
    } catch {
      message.error('Error cargando ejecuciones');
    } finally {
      setListLoading(false);
    }
  }, [agentId, selected]);

  useEffect(() => { fetchList(); }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    setDetailLoading(true);
    fetch(`${EXECUTOR_API}/agent/builder/${agentId}/executions/${selected}`, {
      headers: buildAuthHeaders(),
    })
      .then(r => r.json())
      .then(d => setDetail(d))
      .catch(() => message.error('Error cargando detalle'))
      .finally(() => setDetailLoading(false));
  }, [selected, agentId]);

  if (listLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <Spin size="large" tip="Cargando ejecuciones…" />
      </div>
    );
  }

  if (list.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Aún no hay ejecuciones registradas para este agente"
        style={{ padding: '60px 0' }}
      />
    );
  }

  return (
    <div style={{ display: 'flex', gap: 0 }}>

      {/* Left: list */}
      <div style={{
        width: 260, flexShrink: 0,
        borderRight: '1px solid #f0f0f0',
        overflowY: 'auto', maxHeight: 'calc(100vh - 260px)',
      }}>
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text strong style={{ fontSize: 12 }}>Historial</Text>
          <Tag style={{ fontSize: 10 }}>{total}</Tag>
          <Button
            type="text" size="small" icon={<ReloadOutlined />}
            onClick={() => { setSelected(null); fetchList(); }}
            style={{ marginLeft: 'auto', padding: '0 4px' }}
          />
        </div>
        {list.map(ex => (
          <div
            key={ex.run_id}
            onClick={() => setSelected(ex.run_id)}
            style={{
              padding: '10px 16px',
              borderBottom: '1px solid #fafafa',
              cursor: 'pointer',
              background: selected === ex.run_id ? '#f0f4ff' : '#fff',
              borderLeft: selected === ex.run_id ? `3px solid ${colors.primary}` : '3px solid transparent',
              transition: 'background 0.12s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
              <span style={{
                width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                background: ex.status === 'success' ? '#52c41a' : '#ff4d4f',
              }} />
              <Text style={{ fontSize: 12, color: '#262626', fontWeight: 500, flex: 1, minWidth: 0 }}>
                {formatRelativeTime(ex.started_at)}
              </Text>
              <Text style={{
                fontSize: 11, fontWeight: 600,
                color: durationColor(ex.duration_ms),
              }}>
                {formatDuration(ex.duration_ms)}
              </Text>
            </div>
            <Text type="secondary" style={{ fontSize: 10, paddingLeft: 13 }}>
              {new Date(ex.started_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              {' · '}
              {ex.status === 'success' ? 'completado' : 'error'}
            </Text>
          </div>
        ))}
      </div>

      {/* Right: detail */}
      <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto', maxHeight: 'calc(100vh - 260px)' }}>
        {detailLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
            <Spin tip="Cargando detalle…" />
          </div>
        )}
        {!detailLoading && detail && <ExecutionDetailPanel detail={detail} />}
        {!detailLoading && !detail && (
          <div style={{ textAlign: 'center', color: '#bfbfbf', padding: '60px 0', fontSize: 13 }}>
            Selecciona una ejecución para ver el detalle
          </div>
        )}
      </div>
    </div>
  );
}

// ── Orbital visualization ─────────────────────────────────────────────────────

function OrbitalVisualization({
  spec, agentName, selectedKey, onSelect,
}: {
  spec: Record<string, Record<string, unknown>>;
  agentName: string;
  selectedKey: string | null;
  onSelect: (key: string) => void;
}) {
  return (
    <div className="cog-orbit-wrap" style={{ position: 'relative', width: CONTAINER, height: CONTAINER, margin: '0 auto' }}>
      <style>{`
        @keyframes cog-orbit   { to { transform: rotate(360deg);  } }
        @keyframes cog-counter { to { transform: rotate(-360deg); } }
        @keyframes cog-arc-spin { to { transform: rotate(360deg); } }
        @keyframes cog-glow {
          0%, 100% { opacity: 0.7; }
          50%       { opacity: 1;   }
        }
        @keyframes cog-center-glow {
          0%, 100% { box-shadow: 0 0 24px 6px rgba(118,75,162,0.4), 0 0 0 2px rgba(102,126,234,0.2); }
          50%       { box-shadow: 0 0 40px 12px rgba(102,126,234,0.6), 0 0 0 2px rgba(102,126,234,0.3); }
        }
        .cog-orbit-wrap:hover .cog-spin { animation-play-state: paused; }
        .cog-node { cursor: pointer; transition: transform 0.2s; }
        .cog-node:hover { transform: scale(1.12); }
      `}</style>

      <svg
        style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', zIndex: 0 }}
        width={CONTAINER} height={CONTAINER}
        viewBox={`0 0 ${CONTAINER} ${CONTAINER}`}
      >
        <defs>
          <linearGradient id="arcGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"   stopColor="#667eea" stopOpacity="0.9" />
            <stop offset="50%"  stopColor="#764ba2" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#667eea" stopOpacity="0"   />
          </linearGradient>
        </defs>
        <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="none"
          stroke="rgba(102,126,234,0.45)" strokeWidth="3"
          strokeLinecap="round" strokeDasharray={`${DASH_LEN} ${GAP_LEN}`} />
        <circle className="cog-spin" cx={CENTER} cy={CENTER} r={RADIUS}
          fill="none" stroke="url(#arcGrad)" strokeWidth="3"
          strokeDasharray={`${Math.PI * RADIUS * 0.55} ${Math.PI * RADIUS * 1.45}`}
          strokeLinecap="round"
          style={{ transformOrigin: `${CENTER}px ${CENTER}px`, animation: `cog-arc-spin ${ANIM_SPEED} linear infinite` }} />
      </svg>

      <div className="cog-spin" style={{
        position: 'absolute', width: '100%', height: '100%',
        animation: `cog-orbit ${ANIM_SPEED} linear infinite`,
        transformOrigin: `${CENTER}px ${CENTER}px`, zIndex: 5,
      }}>
        {COMPONENTS.map((comp, i) => {
          const { x, y } = ORBIT_POSITIONS[i];
          const active = !!spec[comp.key];
          const sel    = selectedKey === comp.key;
          return (
            <div key={comp.key} className="cog-spin" style={{
              position: 'absolute', left: x - HALF_ITEM, top: y - HALF_ITEM,
              width: ITEM_SIZE, height: ITEM_SIZE,
              animation: `cog-counter ${ANIM_SPEED} linear infinite`,
              transformOrigin: 'center center',
            }}>
              <div className="cog-node" onClick={() => onSelect(comp.key)} style={{
                width: '100%', height: '100%', borderRadius: '50%',
                background: active
                  ? `radial-gradient(circle at 35% 35%, ${comp.color}, ${comp.color}cc), #f4f3fb`
                  : '#eceefb',
                border: `${sel ? 3 : 2}px solid ${sel ? '#1a1a2e' : (active ? comp.color : 'rgba(102,126,234,0.25)')}`,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                boxShadow: sel
                  ? '0 0 0 4px rgba(26,26,46,0.12), 0 4px 14px rgba(0,0,0,0.18)'
                  : (active ? `0 0 18px ${comp.color}55` : 'none'),
                position: 'relative',
              }}>
                <span style={{ fontSize: 22, lineHeight: 1, animation: active ? 'cog-glow 3s ease-in-out infinite' : 'none' }}>
                  {comp.emoji}
                </span>
                <span style={{ fontSize: 9, marginTop: 3, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase', color: active ? '#fff' : 'rgba(0,0,0,0.35)' }}>
                  {comp.label}
                </span>
                {active && <span style={{ position: 'absolute', top: 3, right: 5, fontSize: 10, color: '#fff' }}>✓</span>}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{
        position: 'absolute', left: CENTER - 54, top: CENTER - 54,
        width: 108, height: 108, borderRadius: '50%',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        animation: 'cog-center-glow 4s ease-in-out infinite', zIndex: 10,
      }}>
        <span style={{ fontSize: 34 }}>🤖</span>
        <span style={{
          color: 'rgba(255,255,255,0.95)', fontSize: 9, fontWeight: 700,
          letterSpacing: 0.5, textTransform: 'uppercase', textAlign: 'center',
          padding: '0 6px', marginTop: 2, lineHeight: 1.3, maxWidth: 88,
          overflow: 'hidden', display: '-webkit-box',
          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        }}>
          {agentName}
        </span>
      </div>
    </div>
  );
}

// ── Component info card ───────────────────────────────────────────────────────

function ComponentInfo({ comp, value }: { comp: ComponentMeta; value: Record<string, unknown> | undefined }) {
  const active = !!value;
  return (
    <div style={{
      border: '1px solid #ececf5', borderTop: `4px solid ${comp.color}`,
      borderRadius: 12, padding: '20px 24px', background: '#fff',
      boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
    }}>
      <Space align="center" style={{ marginBottom: 6 }}>
        <span style={{ fontSize: 24 }}>{comp.emoji}</span>
        <Title level={4} style={{ margin: 0, color: comp.color }}>{comp.label}</Title>
        {active
          ? <Tag color="success" icon={<CheckCircleFilled />}>Configurado</Tag>
          : <Tag color="default">Sin configurar</Tag>}
      </Space>

      <Paragraph style={{ color: '#666', marginBottom: active ? 16 : 0 }}>
        {comp.what}
      </Paragraph>

      {active && (() => {
        const lines = toNaturalLines(comp.key, value!);
        return lines.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
            <Text strong style={{ fontSize: 12, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Cómo está configurado
            </Text>
            {lines.map((line, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span style={{ color: comp.color, fontSize: 16, lineHeight: '22px', flexShrink: 0 }}>●</span>
                <Text style={{ fontSize: 14, lineHeight: '22px' }}>{line}</Text>
              </div>
            ))}
          </div>
        ) : null;
      })()}

      {!active && (
        <Text type="secondary" style={{ fontSize: 13 }}>
          Este componente todavía no se ha configurado para este agente.
        </Text>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

type PageTab = 'components' | 'executions';

const CognitiveAgentDetailPage: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate    = useNavigate();
  const [agent, setAgent]       = useState<AgentSummary | null>(null);
  const [loading, setLoading]   = useState(true);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [activeTab, setActiveTab]     = useState<PageTab>('components');

  const webhookUrl = `${EXECUTOR_API}/agent/builder/webhook/${agentId}`;

  useEffect(() => {
    if (!agentId) return;
    fetch(`${BUILDER_API}/agent/builder/agents`)
      .then(r => r.json())
      .then(data => {
        const found = (data.agents ?? []).find((a: AgentSummary) => a.id === agentId);
        if (!found) { navigate('/'); return; }
        if (!found.cognitiveSpec) { navigate(`/builder/${agentId}`, { replace: true }); return; }
        setAgent(found);
        const spec = found.cognitiveSpec as Record<string, unknown>;
        const first = COMPONENTS.find(c => spec[c.key]);
        if (first) setSelectedKey(first.key);
      })
      .catch(() => message.error('Error cargando agente'))
      .finally(() => setLoading(false));
  }, [agentId, navigate]);

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh', background: '#fafbfc' }}>
        <AppHeader />
        <Content style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
          <Spin size="large" tip="Cargando agente…" />
        </Content>
      </Layout>
    );
  }

  if (!agent) return null;

  const spec       = (agent.cognitiveSpec ?? {}) as Record<string, Record<string, unknown>>;
  const configured = COMPONENTS.filter(c => !!spec[c.key]);
  const selectedComp = COMPONENTS.find(c => c.key === selectedKey) ?? null;

  return (
    <Layout style={{ minHeight: '100vh', background: '#fafbfc' }}>
      <AppHeader />
      <Content>

        {/* ── Top bar ── */}
        <div style={{ background: '#fff', borderBottom: '1px solid #f0f0f0', padding: '14px 24px' }}>
          <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/')} style={{ paddingLeft: 0 }} />
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Space size={8} align="center">
                <Text strong style={{ fontSize: 16 }}>{agent.name}</Text>
                <Tag color="purple" style={{ margin: 0 }}>Cognitivo</Tag>
                <Tag color="success" style={{ margin: 0 }}>{configured.length}/7</Tag>
              </Space>
            </div>

            <div style={{
              marginLeft: 'auto',
              padding: '6px 12px', background: '#f5f5f5', borderRadius: 6,
              display: 'flex', alignItems: 'center', gap: 8, minWidth: 320, maxWidth: 420,
            }}>
              <LinkOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
              <Input
                size="small" value={webhookUrl} readOnly
                style={{ fontSize: 11, fontFamily: 'monospace', border: 'none', background: 'transparent', padding: 0, color: '#595959' }}
                suffix={
                  <Button type="text" size="small" icon={<CopyOutlined />}
                    onClick={() => { navigator.clipboard.writeText(webhookUrl); message.success('Webhook URL copiado'); }}
                    style={{ padding: '0 4px', height: 20 }} />
                }
              />
            </div>

            <Button type="primary" icon={<EditOutlined />} onClick={() => navigate(`/playground?agent_id=${agentId}`)}>
              Editar
            </Button>
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div style={{ background: '#fff', borderBottom: '1px solid #f0f0f0', padding: '0 24px' }}>
          <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', gap: 0 }}>
            {([
              { key: 'components', label: '⚙️ Componentes' },
              { key: 'executions', label: '▶ Ejecuciones' },
            ] as { key: PageTab; label: string }[]).map(tab => {
              const on = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    padding: '12px 20px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 13, fontWeight: on ? 700 : 400,
                    color: on ? colors.primary : '#8c8c8c',
                    borderBottom: on ? `2px solid ${colors.primary}` : '2px solid transparent',
                    transition: 'all 0.15s',
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Components tab ── */}
        {activeTab === 'components' && (
          <>
            <div style={{
              background: 'linear-gradient(180deg, #e8eeff 0%, #f0e8ff 60%, #e8f4ff 100%)',
              padding: '28px 24px 20px',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}>
              <OrbitalVisualization
                spec={spec} agentName={agent.name}
                selectedKey={selectedKey} onSelect={setSelectedKey}
              />
              <Text type="secondary" style={{ fontSize: 13, marginTop: 8 }}>
                Pulsa un componente para ver qué hace y cómo está configurado · pasa el ratón para parar el giro
              </Text>
            </div>

            <div style={{ maxWidth: 760, margin: '0 auto', padding: '24px' }}>
              {selectedComp
                ? <ComponentInfo comp={selectedComp} value={spec[selectedComp.key]} />
                : (
                  <div style={{ textAlign: 'center', color: '#aaa', padding: '40px 0' }}>
                    Selecciona un componente del bucle para ver su detalle
                  </div>
                )}
            </div>
          </>
        )}

        {/* ── Executions tab ── */}
        {activeTab === 'executions' && agentId && (
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <ExecutionsTab agentId={agentId} />
          </div>
        )}

      </Content>
    </Layout>
  );
};

export default CognitiveAgentDetailPage;
